import { useEffect, useRef } from 'react'
import { buildCountryBorderLines } from '../../utils/borders'

// Dash pattern (in line-widths) for each border style. null = solid (reset).
const dashFor = (style) => {
  switch (style) {
    case 'dashed': return [2, 1.5]
    case 'dotted': return [0.4, 1.6]
    case 'mixed': return [3, 1.4, 0.4, 1.4] // dash-dot
    default: return null
  }
}

const HALO_COLOR = '#D5B654'

// Borders + satellite background + country halo.
// - satellite raster visibility (hiding the regions fill under it)
// - active border layer (region vs per-country outlines) with colour / width /
//   opacity / dash style
// - a subtle blurred outer glow around the selected + player's own country
// - lazy per-country outline dissolve (cached per projection) for "Pays" mode
const HALO_PAINT = {
  'line-color': HALO_COLOR,
  'line-width': 5,
  'line-blur': 4,
  'line-opacity': 0.75,
}

export function useBorders(map, {
  satelliteMode, isGlobeView, borderColor, borderWidth, borderMode,
  borderOpacity, borderStyle, haloCountryIds, haloColor, mapBackground, regionsData,
}) {
  const countryBordersCacheRef = useRef({ mercator: null, globe: null })

  // Satellite visibility + border layer selection / style.
  useEffect(() => {
    if (!map.current) return
    const hasSatellite = satelliteMode === 'satellite'
    const activeBorderLayer = borderMode === 'country' ? 'country-border' : 'regions-border'
    const inactiveBorderLayer = borderMode === 'country' ? 'regions-border' : 'country-border'
    const dash = dashFor(borderStyle)
    const opacity = borderOpacity == null ? 1 : borderOpacity

    if (map.current.getLayer('satellite')) {
      map.current.setLayoutProperty('satellite', 'visibility', hasSatellite ? 'visible' : 'none')
    }
    if (map.current.getLayer('regions-fill')) {
      // Keep the layer visible for MapLibre hit-testing. A layer with
      // visibility:'none' — OR fill-opacity:0 — is dropped from
      // queryRenderedFeatures, which breaks the regions-fill click/hover/
      // mousedown handlers that drive build mode, plan mode, infra draw and
      // the ruler in satellite mode. Fade to a near-transparent 0.08 so the
      // satellite imagery shows through while regions stay clickable (same
      // trick the topography overlay uses in MapView.jsx).
      map.current.setLayoutProperty('regions-fill', 'visibility', 'visible')
      map.current.setPaintProperty('regions-fill', 'fill-opacity', hasSatellite ? 0.08 : 0.8)
    }

    if (map.current.getLayer(inactiveBorderLayer)) {
      map.current.setLayoutProperty(inactiveBorderLayer, 'visibility', 'none')
    }
    if (map.current.getLayer(activeBorderLayer)) {
      const visible = borderMode !== 'none'
      map.current.setLayoutProperty(activeBorderLayer, 'visibility', visible ? 'visible' : 'none')
      map.current.setPaintProperty(activeBorderLayer, 'line-color', borderColor || '#333333')
      map.current.setPaintProperty(activeBorderLayer, 'line-width', borderWidth || 1)
      map.current.setPaintProperty(activeBorderLayer, 'line-opacity', opacity)
      // null resets to the default (solid).
      map.current.setPaintProperty(activeBorderLayer, 'line-dasharray', dash || null)
    }
    if (!isGlobeView && map.current.getLayer('background')) {
      map.current.setPaintProperty('background', 'background-color', mapBackground || '#1a1a1a')
    }
  }, [map, satelliteMode, isGlobeView, borderColor, borderWidth, borderMode, borderOpacity, borderStyle, mapBackground])

  // Country halo: a soft blurred glow around the selected + own country. Two
  // layers so the glow matches the active border mode:
  //  - country-halo         : glows region outlines (regions source) — default
  //  - country-halo-outline : glows the dissolved country outline
  //    (countryBordersSource) — used in "country" border mode so the halo traces
  //    the country's edge instead of every internal regional border.
  // Both created once, then only filter/visibility change.
  useEffect(() => {
    if (!map.current || !map.current.getLayer('regions-fill')) return
    const ids = (haloCountryIds || []).filter((v) => v != null)

    if (!map.current.getLayer('country-halo')) {
      map.current.addLayer({
        id: 'country-halo',
        type: 'line',
        source: 'regions',
        layout: { 'line-cap': 'round', 'line-join': 'round', visibility: 'none' },
        paint: HALO_PAINT,
      }, 'regions-hover')
    }
    if (!map.current.getLayer('country-halo-outline') && map.current.getSource('countryBordersSource')) {
      map.current.addLayer({
        id: 'country-halo-outline',
        type: 'line',
        source: 'countryBordersSource',
        layout: { 'line-cap': 'round', 'line-join': 'round', visibility: 'none' },
        paint: HALO_PAINT,
      }, 'regions-hover')
    }

    const filter = ['in', ['get', 'country_id'], ['literal', ids]]
    const useOutline = borderMode === 'country'
    const color = haloColor || HALO_COLOR
    if (map.current.getLayer('country-halo')) {
      map.current.setFilter('country-halo', filter)
      map.current.setPaintProperty('country-halo', 'line-color', color)
      map.current.setLayoutProperty('country-halo', 'visibility', ids.length && !useOutline ? 'visible' : 'none')
    }
    if (map.current.getLayer('country-halo-outline')) {
      map.current.setFilter('country-halo-outline', filter)
      map.current.setPaintProperty('country-halo-outline', 'line-color', color)
      map.current.setLayoutProperty('country-halo-outline', 'visibility', ids.length && useOutline ? 'visible' : 'none')
    }
  }, [map, haloCountryIds, regionsData, borderMode, haloColor])

  // Country border mode: collapse region polygons into per-country outlines
  // (see utils/borders.js), lazily and only once per projection. Cached so
  // switching modes back and forth is instant.
  useEffect(() => {
    if (!map.current || borderMode !== 'country' || !regionsData) return
    const projectionKey = isGlobeView ? 'globe' : 'mercator'
    const source = map.current.getSource('countryBordersSource')
    if (!source) return

    const cached = countryBordersCacheRef.current[projectionKey]
    if (cached) {
      source.setData(cached)
      return
    }

    const result = buildCountryBorderLines(regionsData.features)
    countryBordersCacheRef.current[projectionKey] = result
    source.setData(result)
  }, [map, borderMode, regionsData, isGlobeView])
}
